import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Flight as FlightIcon,
  Inventory as InventoryIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Assessment as AnalyticsIcon,
  Settings as SettingsIcon,
  Build as MaintenanceIcon,
  Assignment as ChecklistIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import styles from '../../styles/UserGuides.module.css';

function UserGuides() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const guides = [
    {
      title: 'LAANC Operations Guide',
      description: 'Complete guide to creating and managing LAANC authorization requests.',
      icon: <FlightIcon fontSize="large" />,
      category: 'Operations',
      level: 'Beginner',
      topics: [
        'Understanding LAANC',
        'Creating your first operation',
        'Selecting flight areas',
        'Setting altitude limits',
        'Managing authorizations',
        'Canceling operations'
      ],
      path: null // Future: link to detailed guide
    },
    {
      title: 'Fleet Management Guide',
      description: 'Learn how to track drones, batteries, and equipment in your fleet inventory.',
      icon: <InventoryIcon fontSize="large" />,
      category: 'Fleet',
      level: 'Intermediate',
      orgOnly: true,
      topics: [
        'Adding drones to inventory',
        'Uploading drone photos',
        'Managing battery inventory',
        'Tracking payload and sensors',
        'Setting up maintenance schedules',
        'Understanding fleet limits'
      ],
      path: null
    },
    {
      title: 'Maintenance Tracking Guide',
      description: 'Track maintenance history, schedule service, and log repairs for your fleet.',
      icon: <MaintenanceIcon fontSize="large" />,
      category: 'Fleet',
      level: 'Intermediate',
      orgOnly: true,
      topics: [
        'Logging maintenance activities',
        'Maintenance types and categories',
        'Cost tracking',
        'Parts replacement records',
        'Scheduled maintenance alerts',
        'Maintenance history reports'
      ],
      path: null
    },
    {
      title: 'Compliance & Safety Guide',
      description: 'Managing pilot licenses, pre-flight checklists, and incident reporting.',
      icon: <SecurityIcon fontSize="large" />,
      category: 'Compliance',
      level: 'Beginner',
      orgOnly: true,
      topics: [
        'Uploading pilot certificates',
        'License expiration tracking',
        'Creating checklist templates',
        'Completing pre-flight checklists',
        'Reporting incidents',
        'Understanding compliance dashboard'
      ],
      path: null
    },
    {
      title: 'Pre-Flight Checklists Guide',
      description: 'Create templates, complete checklists, and maintain safety standards.',
      icon: <ChecklistIcon fontSize="large" />,
      category: 'Compliance',
      level: 'Beginner',
      orgOnly: true,
      topics: [
        'Using checklist templates',
        'Creating custom checklists',
        'Draft and submit workflow',
        'Completing checklist items',
        'Exporting checklists to PDF',
        'Viewing checklist history'
      ],
      path: null
    },
    {
      title: 'Team Management Guide',
      description: 'Inviting members, assigning roles, and managing permissions.',
      icon: <PeopleIcon fontSize="large" />,
      category: 'Organization',
      level: 'Intermediate',
      orgOnly: true,
      topics: [
        'Inviting team members',
        'Understanding user roles',
        'Assigning permissions',
        'Managing pending invitations',
        'Removing team members',
        'Role hierarchy and access control'
      ],
      path: null
    },
    {
      title: 'Analytics & Reporting Guide',
      description: 'Understanding your flight analytics, utilization reports, and cost analysis.',
      icon: <AnalyticsIcon fontSize="large" />,
      category: 'Analytics',
      level: 'Advanced',
      orgOnly: true,
      topics: [
        'Flight analytics dashboard',
        'Utilization reports',
        'Cost analysis metrics',
        'Fleet performance insights',
        'Team activity tracking',
        'Exporting reports'
      ],
      path: null
    },
    {
      title: 'Organization Settings Guide',
      description: 'Configure your organization profile, billing, and integrations.',
      icon: <SettingsIcon fontSize="large" />,
      category: 'Organization',
      level: 'Intermediate',
      orgOnly: true,
      topics: [
        'Organization profile setup',
        'Managing subscription',
        'Billing and payment methods',
        'Understanding plan limits',
        'Upgrading your plan',
        'Organization identifiers'
      ],
      path: null
    }
  ];

  const filteredGuides = searchQuery
    ? guides.filter(guide =>
        guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        guide.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        guide.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        guide.topics.some(topic => topic.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : guides;

  const categoryColors = {
    'Operations': 'primary',
    'Fleet': 'secondary',
    'Compliance': 'error',
    'Organization': 'info',
    'Analytics': 'success'
  };

  const levelColors = {
    'Beginner': 'success',
    'Intermediate': 'warning',
    'Advanced': 'error'
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          User Guides & Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Comprehensive guides to help you master FlightLoop features.
        </Typography>

        <TextField
          fullWidth
          placeholder="Search guides by title, category, or topic..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 4 }}
        />

        <Divider sx={{ mb: 4 }} />

        {filteredGuides.length === 0 ? (
          <Typography variant="body1" color="text.secondary" align="center">
            No guides found matching your search.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {filteredGuides.map((guide, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ color: 'primary.main', flexShrink: 0 }}>
                        {guide.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {guide.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={guide.category}
                            size="small"
                            color={categoryColors[guide.category]}
                          />
                          <Chip
                            label={guide.level}
                            size="small"
                            color={levelColors[guide.level]}
                            variant="outlined"
                          />
                          {guide.orgOnly && (
                            <Chip
                              label="Org Only"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {guide.description}
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Topics Covered:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                      {guide.topics.slice(0, 4).map((topic, i) => (
                        <Typography
                          component="li"
                          variant="body2"
                          color="text.secondary"
                          key={i}
                          sx={{ mb: 0.5 }}
                        >
                          {topic}
                        </Typography>
                      ))}
                      {guide.topics.length > 4 && (
                        <Typography
                          component="li"
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic' }}
                        >
                          +{guide.topics.length - 4} more topics
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      disabled={!guide.path}
                    >
                      {guide.path ? 'Read Guide' : 'Coming Soon'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Video Tutorials Section */}
        <Divider sx={{ my: 6 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Video Tutorials
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Coming soon: Watch step-by-step video tutorials for all major features.
          </Typography>
        </Box>

        {/* Need More Help */}
        <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Can't Find What You're Looking For?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Contact our support team for personalized assistance.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/contactus')}
          >
            Contact Support
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default UserGuides;
